import { process } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { GridColumn as Column, Grid, GridToolbar } from "@progress/kendo-react-grid";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Popup } from "@progress/kendo-react-popup";
import { columnsIcon } from "@progress/kendo-svg-icons";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  dataConnectionColumnPopupOffset,
  selectedDataConnectionItem,
  showDataConnectionColumnPopup,
  templateData,
} from "../Template/NewTemplateSignals";

const getDataColumnsTitles = () => {
  let columns = {};
  columns = Object.keys(selectedDataConnectionItem.value?.columns);
  return columns;
};

export const GridHelper = (props) => {
  const { showColumnsConfigurator } = props.toolbarSettings ?? {};
  const GridProps = props.children.props;

  // Needed for toggling features from the configurator
  const defaultConfiguration = {
    filterable: props.filterable ?? GridProps.filterable,
    selectable: props.selectable ?? GridProps.selectable,
    sortable: props.sortable ? props.sortable : GridProps.sortable,
    groupable: props.groupable ?? GridProps.groupable,
    pageable: props.pageable ?? GridProps.pageable,
  };
  const [configuration, setConfiguration] = useState({
    ...defaultConfiguration,
  });
  useEffect(() => {
    setConfiguration({
      ...configuration,
      pageable: props.pageable ?? GridProps.pageable,
    });
  }, [props.pageable, GridProps.pageable]);
  useEffect(() => {
    setConfiguration({
      ...configuration,
      filterable: props.filterable ?? GridProps.filterable,
    });
  }, [props.filterable, GridProps.filterable]);
  useEffect(() => {
    setConfiguration({
      ...configuration,
      groupable: props.groupable ?? GridProps.groupable,
    });
  }, [props.groupable, GridProps.groupable]);
  useEffect(() => {
    setConfiguration({
      ...configuration,
      selectable: props.selectable ?? GridProps.selectable,
    });
  }, [props.selectable, GridProps.selectable]);
  useEffect(() => {
    setDefaultData(props.data ?? GridProps.data);
    setData(props.data ?? GridProps.data);
  }, [props.data, GridProps.data]);

  // The raw data
  const [defaultData, setDefaultData] = useState(
    (props.data ?? GridProps.data).map((dataItem) => dataItem)
  );
  const [data, setData] = useState(defaultData);
  const [dataState, setDataState] = useState(
    props.initialDataState ?? {
      skip: null,
    }
  );
  const [columnsState, setColumnsState] = useState(getDataColumnsTitles());
  const [collapsedState, setCollapsedState] = useState([]);
  const [total, setTotal] = useState(data.length);

  // Updates the columns visibility state
  const updateColumns = (setting, state) => {
    let newState = {
      ...columnsState,
    };
    newState[setting] = state;
    setColumnsState(newState);
  };
  const onDataStateChange = (ev) => {
    setDataState(ev.dataState);
  };

  // All data operations, except the external filter, are handled here
  const finalData = useMemo(() => {
    let processedData = data.map((item) => ({
      ...item,
    }));
    processedData = process(processedData, dataState);
    setTotal(processedData.total);
    return processedData;
  }, [data, dataState, setDataState, collapsedState, setCollapsedState]);
  const newProps = {
    ...props.children.props,
    children: null,
    data: finalData,
    onDataStateChange: onDataStateChange,
    ...dataState,
    total: total,
    expandField: props.expandedField ?? GridProps.expandedField ?? "expanded",
    selectedField: props.selectedField ?? GridProps.selectedField ?? "selected",
  };

  const gridChildren = useMemo(() => {
    const toolBar = GridProps.children.find((c) => c.type.displayName === "KendoReactGridToolbar");
    const gridToolBar = (
      <GridToolbar {...toolBar?.props}>
        {toolBar?.props?.children && <div>{toolBar.props.children}</div>}

        <div
          style={{
            right: "15px",
            float: "right",
            position: "absolute",
            display: "flex",
            gap: "8px",
          }}
        >
          <div>
            {showColumnsConfigurator && (
              <ColumnsButton
                onChange={updateColumns}
                options={columnsState}
              />
            )}
          </div>
        </div>
      </GridToolbar>
    );
    let children = GridProps.children.map((child, index) => {
      if (child.type.displayName === "KendoReactGridColumn") {
        return (
          <Column
            key={child.type.displayName + "_" + index}
            {...child.props}
          />
        );
      } else if (child.type.displayName === "KendoReactGridToolbar") {
        return null;
      } else {
        return child;
      }
    });
    if (props.toolbarSettings) {
      children.push(gridToolBar);
    } else if (toolBar) {
      children.push(toolBar);
    }
    return children;
  }, [GridProps.children, columnsState, collapsedState, configuration, dataState]);
  const gridRef = useRef(null);
  const renderedGridProps = {
    ...newProps,
    ...configuration,
    children: gridChildren,
    ref: gridRef,
  };
  const GridComponent = <Grid {...renderedGridProps} />;
  return <>{GridComponent}</>;
};

GridHelper.propTypes = {
  toolbarSettings: PropTypes.object,
  children: PropTypes.node.isRequired,
  filterable: PropTypes.bool,
  selectable: PropTypes.bool,
  sortable: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  groupable: PropTypes.bool,
  pageable: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  data: PropTypes.array,
  initialDataState: PropTypes.object,
  expandedField: PropTypes.string,
  selectedField: PropTypes.string,
};

// Columns show/hide
const ColumnsButton = (props) => {
  return (
    <ConfiguratorButtonBase
      {...props}
      icon={columnsIcon}
      title="Show/Hide Columns"
    />
  );
};

ColumnsButton.propTypes = {
  onChange: PropTypes.func,
  options: PropTypes.object,
};

// Button showing popup with all properties of an object
// props.options is the object with the properties that will be rendered in the checkbox list
// props.icon is the name of the button icon
// props.title sets the title of the button
// props.onChange returns the toggled option/property
const ConfiguratorButtonBase = (props) => {
  const buttonRef = useRef(null);

  return (
    <>
      <span
        title={props.title}
        ref={buttonRef}
      >
        <Button
          onClick={() => {
            if (buttonRef.current) {
              dataConnectionColumnPopupOffset.value = buttonRef.current.getBoundingClientRect();
            }
            showDataConnectionColumnPopup.value = !showDataConnectionColumnPopup.value;
          }}
          themeColor={"primary"}
          size="small"
        >
          <SvgIcon
            icon={props.icon}
            size="medium"
          />
        </Button>
      </span>
      <Popup
        margin={{
          vertical: 25,
        }}
        offset={dataConnectionColumnPopupOffset.value}
        show={showDataConnectionColumnPopup.value}
        animate={{ openDuration: 0, closeDuration: 0 }}
      >
        <div
          className="configurator-wrap-element"
          style={{
            padding: "10px",
            backgroundColor: "#fefefe",
            border: "1px solid #ddd",
          }}
        >
          <div
            style={{
              paddingBottom: "5px",
            }}
          >
            {props.title}:
          </div>
          <div>
            {Object.keys(selectedDataConnectionItem.value.columns).map((column, index) => {
              let checked = selectedDataConnectionItem.value.columns[column].selected;
              return (
                <div
                  style={{
                    padding: "3px 5px 5px 0px",
                  }}
                  className="k-d-flex"
                  key={column + "_" + index}
                >
                  <Checkbox
                    className="k-mr-md"
                    value={checked}
                    onChange={() => {
                      // Create a shallow copy of the DataConnections array
                      const newDataConnections = [...templateData.value.properties.DataConnections];
                      const dataConnectionIndex = newDataConnections.findIndex(
                        (connection) => connection.id === selectedDataConnectionItem.value.id
                      );
                      // Create a new columns object with the updated 'selected' value
                      const newColumns = {
                        ...newDataConnections[dataConnectionIndex].columns,
                        [column]: {
                          ...newDataConnections[dataConnectionIndex].columns[column],
                          selected: !checked,
                        },
                      };

                      // Update the first element of the DataConnections array
                      newDataConnections[dataConnectionIndex] = {
                        ...newDataConnections[dataConnectionIndex],
                        columns: newColumns,
                      };

                      selectedDataConnectionItem.value = newDataConnections[dataConnectionIndex];

                      // Finally, update the templateData with the new DataConnections array
                      templateData.value = {
                        ...templateData.value,
                        properties: {
                          ...templateData.value.properties,
                          DataConnections: newDataConnections,
                        },
                      };

                      console.log(templateData.value);
                    }}
                    data-attr={column}
                  />{" "}
                  {column}
                </div>
              );
            })}
          </div>
        </div>
      </Popup>
    </>
  );
};

ConfiguratorButtonBase.propTypes = {
  icon: PropTypes.object,
  title: PropTypes.string,
};
